import React, { useCallback, useEffect, useRef, useState } from 'react';
import { Bell, Check, Loader2, RefreshCw } from 'lucide-react';
import toast from 'react-hot-toast';
import {
  getDektalNotifications,
  markDektalNotificationRead,
} from '../services/dektalNotificationApi';

function readableTime(value) {
  if (!value) return '';
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return '';
  return new Intl.DateTimeFormat('en-IN', {
    dateStyle: 'medium',
    timeStyle: 'short',
  }).format(date);
}

export default function DektalNotificationsPanel({ active, onUnreadChange, onNavigateAction }) {
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const markingRef = useRef(new Set());

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const payload = await getDektalNotifications({
        includeRead: true,
        includeDismissed: false,
        includeArchived: false,
        limit: 50,
      });
      const nextRows = Array.isArray(payload?.rows) ? payload.rows : [];
      setRows(nextRows);
      setError('');
      onUnreadChange?.(nextRows.filter((item) => !item.readAt && item.status !== 'read').length);
    } catch (requestError) {
      const message = requestError?.response?.data?.error || 'Unable to load your notifications.';
      setError(message);
    } finally {
      setLoading(false);
    }
  }, [onUnreadChange]);

  useEffect(() => {
    if (active) load();
  }, [active, load]);

  const markRead = async (notification) => {
    if (!notification?.id || notification.readAt || notification.status === 'read' || markingRef.current.has(notification.id)) return;
    markingRef.current.add(notification.id);
    try {
      await markDektalNotificationRead(notification.id);
      await load();
    } catch (requestError) {
      toast.error(requestError?.response?.data?.error || 'Unable to mark this notification as read.');
    } finally {
      markingRef.current.delete(notification.id);
    }
  };

  return (
    <div className="flex h-full min-h-0 flex-col bg-slate-50" aria-label="Dektal notifications">
      <div className="flex shrink-0 items-center justify-between border-b border-slate-200 bg-white px-4 py-3">
        <p className="text-sm text-slate-600">Clinic notifications for your account</p>
        <button type="button" onClick={load} disabled={loading} className="inline-flex min-h-10 items-center gap-2 rounded-full border border-slate-200 px-3 text-xs font-semibold text-slate-700 disabled:opacity-60" aria-label="Refresh notifications">
          <RefreshCw className={`h-4 w-4 ${loading ? 'animate-spin' : ''}`} /> Refresh
        </button>
      </div>
      <div className="min-h-0 flex-1 overflow-y-auto overscroll-contain p-3 sm:p-4">
        {error ? <div role="alert" className="mb-3 rounded-xl border border-rose-200 bg-rose-50 p-3 text-sm text-rose-700">{error}</div> : null}
        {loading && rows.length === 0 ? <div className="flex justify-center py-12"><Loader2 className="h-6 w-6 animate-spin text-blue-600" /></div> : null}
        {!loading && rows.length === 0 && !error ? <div className="py-14 text-center"><Bell className="mx-auto h-8 w-8 text-slate-300" /><p className="mt-3 text-sm text-slate-500">You have no notifications right now.</p></div> : null}
        <div className="space-y-3">
          {rows.map((notification) => {
            const unread = !notification.readAt && notification.status !== 'read';
            return (
              <article key={notification.id} className={`min-w-0 rounded-2xl border p-4 ${unread ? 'border-blue-200 bg-blue-50/70' : 'border-slate-200 bg-white'}`}>
                <div className="flex min-w-0 items-start justify-between gap-3">
                  <div className="min-w-0"><h3 className="break-words text-sm font-bold text-slate-900">{notification.title || 'Dektal notification'}</h3><p className="mt-1 break-words text-sm leading-6 text-slate-600">{notification.message}</p><time className="mt-2 block text-xs text-slate-400" dateTime={notification.createdAt}>{readableTime(notification.createdAt)}</time></div>
                  {unread ? <span className="mt-1 h-2.5 w-2.5 shrink-0 rounded-full bg-blue-600"><span className="sr-only">Unread</span></span> : null}
                </div>
                <div className="mt-3 flex flex-wrap gap-2">
                  {unread ? <button type="button" onClick={() => markRead(notification)} className="inline-flex min-h-10 items-center gap-1.5 rounded-full border border-blue-200 bg-white px-3 text-xs font-semibold text-blue-700"><Check className="h-3.5 w-3.5" />Mark read</button> : <span className="inline-flex min-h-10 items-center text-xs font-semibold text-slate-400">Read</span>}
                  {notification.actionRoute ? <button type="button" onClick={() => onNavigateAction?.({ label: notification.actionLabel || 'Open', route: notification.actionRoute, params: notification.actionParamsJson }, { source: 'notification', notification })} className="min-h-10 rounded-full bg-blue-600 px-4 text-xs font-semibold text-white">{notification.actionLabel || 'Open'}</button> : null}
                </div>
              </article>
            );
          })}
        </div>
      </div>
    </div>
  );
}
